import { memo } from 'react';
import { AlertBanner, Badge, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Shield } from 'lucide-react';

import { ROLE_KIND_META } from '../../constants/copy.js';
import { useInviteMember } from '../../hooks/useInviteMember';
import StudioBadge from '../shared/StudioBadge';
import SelectableCard from './SelectableCard';

/**
 * Roles step (Member only): multi-select role cards. Studio access follows from the roles.
 *
 * @param {object} props
 * @param {Array<{ id: string, name: string, kind: string, description: string,
 *   studios: Array<{ id: string, name: string }> }>} props.roles
 * @param {boolean} [props.loading] Roles are still loading.
 * @param {boolean} [props.error] Roles could not be loaded.
 */
function RolesStep({ roles, loading = false, error = false }) {
  const { t } = useTranslation('people-access');
  const { state, toggleRole } = useInviteMember();

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="m-0 text-sm font-semibold text-content">{t('invite.roles.label')}</h2>
        <p className="m-0 text-xs text-content-muted">{t('invite.roles.helper')}</p>
      </div>

      {loading && <Skeleton height="72px" borderRadius="8px" />}
      {error && (
        <AlertBanner tone="danger" title={t('invite.options.errorTitle')}>
          {t('invite.options.errorBody')}
        </AlertBanner>
      )}

      {!loading && !error && roles.length === 0 && (
        <StatusMessage icon={Shield} title={t('invite.roles.empty')} />
      )}
      {!loading && !error && roles.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {roles.map((role) => {
            const kind = ROLE_KIND_META[role.kind];
            return (
              <SelectableCard
                key={role.id}
                value={role.id}
                selected={state.roleIds.includes(role.id)}
                ariaLabel={t('invite.roles.selectRole', { name: role.name })}
                onSelect={toggleRole}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-content">{role.name}</span>
                  {kind && <Badge variant={kind.variant}>{t(kind.labelKey)}</Badge>}
                </div>
                <p className="m-0 mt-1 text-xs text-content-muted">{role.description}</p>
                {role.studios.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {role.studios.map((studio) => (
                      <StudioBadge key={studio.id} id={studio.id} name={studio.name} />
                    ))}
                  </div>
                )}
              </SelectableCard>
            );
          })}
        </div>
      )}

      {state.roleIds.length === 0 && (
        <p className="m-0 text-sm text-content-muted">{t('invite.roles.hint')}</p>
      )}
    </section>
  );
}

export default memo(RolesStep);
